/**
 * Help-article registry.
 *
 * Each article is a small JS module exporting an object:
 *
 *     {
 *       slug:        'qr-codes',                 // URL slug — /help/:slug
 *       title:       'QR codes',                 // visible title
 *       category:    'Core features',            // sidebar grouping
 *       order:       1,                          // within-category sort
 *       requiredRole?: 'admin' | 'editor',       // optional gating
 *       body:        '# QR codes\n\n…markdown…', // article body
 *     }
 *
 * Articles are bundled at build time — no runtime fetch, no SaaS doc
 * tool. To add an article: create `frontend/src/help/articles/<slug>.js`
 * and append it to the `ARTICLES` array below.
 */

import { welcome } from './articles/welcome';

export const ARTICLES = [
  welcome,
];

/**
 * Return articles the given user can see. An article with no
 * `requiredRole` is visible to anyone signed in; an article gated on
 * 'admin' is visible only to admins; 'editor' is visible to admins and
 * editors.
 *
 * The role ranking matches the backend's hierarchy: admin > editor > viewer.
 */
export function articlesForRole(role) {
  return ARTICLES.filter((a) => isArticleVisible(a, role)).sort((a, b) => {
    if (a.category !== b.category) return a.category.localeCompare(b.category);
    if (a.order !== b.order) return (a.order ?? 999) - (b.order ?? 999);
    return a.title.localeCompare(b.title);
  });
}

export function articleBySlug(slug, role) {
  const article = ARTICLES.find((a) => a.slug === slug);
  if (!article) return null;
  return isArticleVisible(article, role) ? article : null;
}

export function articlesByCategory(role) {
  const grouped = new Map();
  for (const article of articlesForRole(role)) {
    if (!grouped.has(article.category)) grouped.set(article.category, []);
    grouped.get(article.category).push(article);
  }
  return Array.from(grouped.entries()).map(([category, items]) => ({
    category,
    items,
  }));
}

function isArticleVisible(article, role) {
  if (!article.requiredRole) return true;
  if (article.requiredRole === 'admin') return role === 'admin';
  if (article.requiredRole === 'editor') {
    return role === 'admin' || role === 'editor';
  }
  return true;
}
